import React, { FC } from 'react';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Button, ButtonMode } from '@components/atoms/Button';
import styles from './CtaBanner.module.css';

export type CtaBannerVariant = 'side' | 'background';
export type CtaBannerTextColor = 'light' | 'dark';

export interface CtaBannerProps {
    /** Texto do subtítulo superior (ex: "FAÇA PARTE") */
    subtitle?: string | null;
    /** Título principal do banner */
    title: string;
    /** Descrição do banner (opcional) */
    description?: string | null;
    /** Texto do botão (se não fornecido, o botão não é renderizado) */
    buttonText?: string | null;
    /** Callback ao clicar no botão */
    onButtonClick?: () => void;
    /** URL da imagem */
    imageUrl?: string | null;
    /** Alt da imagem */
    imageAlt?: string | null;
    /** Variante do banner: 'side' (imagem lateral) ou 'background' (imagem de fundo) */
    variant?: CtaBannerVariant;
    /** Cor do texto: 'light' (branco) ou 'dark' (preto). Padrão: 'light' para background, 'dark' para side */
    textColor?: CtaBannerTextColor;
    /** Classes CSS adicionais para a section */
    classes?: string;
}

export const CtaBanner: FC<CtaBannerProps> = ({
    subtitle,
    title,
    description,
    buttonText,
    onButtonClick,
    imageUrl,
    imageAlt,
    variant = 'side',
    textColor,
    classes,
}) => {
    const isBackgroundVariant = variant === 'background';
    const resolvedTextColor = textColor || (isBackgroundVariant ? 'light' : 'dark');

    const sectionClasses = [
        styles.ctaSection,
        isBackgroundVariant ? styles.backgroundVariant : styles.sideVariant,
        classes,
    ]
        .filter(Boolean)
        .join(' ');

    const buttonClasses = resolvedTextColor === 'light' ? styles.buttonLight : '';

    const sectionStyle = isBackgroundVariant && imageUrl
        ? { backgroundImage: `url(${imageUrl})` }
        : undefined;

    const labelClasses = [
        styles.ctaLabel,
        resolvedTextColor === 'light' ? styles.labelLight : styles.labelDark,
    ].join(' ');

    const titleClasses = [
        styles.ctaTitle,
        resolvedTextColor === 'light' ? styles.titleLight : styles.titleDark,
    ].join(' ');

    const subtitleClasses = [
        styles.ctaSubtitle,
        resolvedTextColor === 'light' ? styles.subtitleLight : styles.subtitleDark,
    ].join(' ');

    return (
        <section className={sectionClasses} style={sectionStyle}>
            {isBackgroundVariant && (
                <div className={resolvedTextColor === 'light' ? styles.overlayDark : styles.overlayLight} />
            )}
            <Container>
                <div className={styles.ctaContent}>
                    <div className={styles.ctaText}>
                        {subtitle && (
                            <Typography
                                tagVariant={TypographyTagVariants.span}
                                classes={labelClasses}
                            >
                                {subtitle}
                            </Typography>
                        )}
                        <Typography
                            tagVariant={TypographyTagVariants.h2}
                            classes={titleClasses}
                        >
                            {title}
                        </Typography>
                        {description && (
                            <Typography classes={subtitleClasses}>
                                {description}
                            </Typography>
                        )}
                        {buttonText && (
                            <Button
                                mode={ButtonMode.secondary}
                                label={buttonText}
                                onClick={onButtonClick}
                                classes={buttonClasses}
                            />
                        )}
                    </div>
                    {!isBackgroundVariant && imageUrl && (
                        <div className={styles.ctaImage}>
                            <img
                                src={imageUrl}
                                alt={imageAlt || title}
                                className={styles.personImage}
                            />
                        </div>
                    )}
                </div>
            </Container>
        </section>
    );
};
